"use client";
import { motion } from "framer-motion";
import { fadeUp, stagger } from "../../../../components/motionVariants";
import { Zap, ArrowRight } from "lucide-react";
import Link from "next/link";
import DocsPageFrame from "../../../components/DocsPageFrame";
import Callout from "../../../components/Callout";
import Screenshot from "../../../components/Screenshot";

const RULES = [
  {
    topic: "Subscription gate",
    rule: "Creating a production order requires the Production Order feature to be active on your plan. Nothing else in this module is plan-gated",
  },
  {
    topic: "Production Order Code",
    rule: "Auto-generated with a PRO prefix, the same custom-prefix config icon used elsewhere in All Master",
  },
  {
    topic: "All three warehouses are required",
    rule: "Raw Material, Production, and Finished Goods Warehouse must all be picked, every time, for both production methods. They can be the same warehouse",
  },
  {
    topic: "No Production Date field",
    rule: "There isn't one. The order's creation timestamp is stored automatically; nothing on this form lets you set or backdate it",
  },
  {
    topic: "No Notes field",
    rule: "The Add Production form has no notes or remarks field at all",
  },
  {
    topic: "No insufficient-stock warning",
    rule: "The raw material stock table is a plain read-only display. Nothing turns red or blocks you for having too little stock",
  },
  {
    topic: "Editing",
    rule: "Not possible once created. There's no field-level edit for item, quantity, warehouses, or raw materials, only submit, delete, or close",
  },
  {
    topic: "Deleting",
    rule: "Only allowed on an order that's still Pending or Waiting for Inventory Approval, with zero submissions, no process ever started, and any related inventory transfer request still Pending",
  },
];

const sections = [
  {
    heading: "What this module does",
    subsections: [
      { heading: "1. Every order inherits its method from the item, not from this form" },
      { heading: "2. Three warehouses, not two" },
      { heading: "3. What actually happens when you save depends on the method" },
    ],
  },
  { heading: "Business rules" },
];

const Definitions = ({ items }) => (
  <div className="space-y-2.5">
    {items.map((item) => (
      <p key={item.term} className="text-[16px] geist-regular text-[#374151] leading-relaxed">
        <span className="geist-medium text-[#111827]">{item.term}:</span> {item.desc}
      </p>
    ))}
  </div>
);

export default function AddProductionPage() {
  return (
    <DocsPageFrame sections={sections}>
      <motion.div
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.05 }}
        variants={stagger}
      >
        {/* Header */}
        <motion.div variants={fadeUp} className="mb-10">
          <h1 className="text-[30px] leading-[1.2] text-[#111827] sentient-bold mb-4">
            Add Production
          </h1>
          <h2 className="text-[24px] sentient-medium text-[#111827] mb-3">
            For: Anyone starting a manufacturing run for a finished item
          </h2>
          <p className="text-[16px] sm:text-[17px] leading-[1.75] text-[#374151] geist-regular max-w-2xl">
            <span className="geist-medium text-[#111827]">Add Production</span>{" "}
            creates the order that a run is tracked against. What it asks for,
            and what happens the moment you save, depends entirely on how the
            item you&apos;re producing was set up in Item Master, not on any
            choice you make on this screen. Nothing here moves stock or posts
            to your ledger yet, that only starts once the order is approved
            and submitted, but this is where the warehouses, the item, and
            the optional Sales Order link that those later effects depend on
            get locked in.
          </p>
        </motion.div>

        <hr className="my-8" />

        {/* What this module does */}
        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-0"
            className="text-[24px] sentient-medium text-[#111827] mb-6 scroll-mt-24"
          >
            What this module does
          </h2>

          {/* 1. Method inherited */}
          <h3
            id="section-0-sub-0"
            className="text-[18px] sentient-medium text-[#111827] mb-3 scroll-mt-24"
          >
            1. Every order inherits its method from the item, not from this form
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            There&apos;s no method selector here at all. You pick an{" "}
            <span className="geist-medium text-[#111827]">Item</span>, and the
            form fills in a read-only{" "}
            <span className="geist-medium text-[#111827]">
              Production Manage Method
            </span>{" "}
            field with whatever that item&apos;s own Production Data tab
            already says.
          </p>
          <Definitions
            items={[
              {
                term: "BOM Based",
                desc: "The item carries its own bill of materials. This form auto-fills a raw material line per BOM entry, quantity multiplied by how many units you're planning to produce.",
              },
              {
                term: "Process Route",
                desc: "The item points at a Recipe. This form shows the Recipe's name and its ordered list of Process steps, nothing about materials, because a Recipe never carries any.",
              },
              {
                term: "Item not set up yet",
                desc: "An \"Add Production Item\" button opens a drawer that writes the BOM or Recipe choice onto the item itself, the same place Item Master's Production Data tab does. It configures the item, not this order.",
              },
              {
                term: "Item and Quantity",
                desc: "The item is labelled just \"Item\", and the quantity field is labelled \"Quantity Info\", not \"Finished Item\" or \"Planned Qty\".",
              },
            ]}
          />
          <Callout type="note">
            You can optionally check &quot;Production By Sales order&quot; and
            pick a Sales Order first. That pre-fills the item and quantity
            from the order&apos;s own line, and the finished production order
            carries a link back to it.
          </Callout>
          <Screenshot caption="Screenshot: the Add Production form with a BOM Based item selected, showing the auto-filled raw material lines." />

          {/* 2. Three warehouses */}
          <h3
            id="section-0-sub-1"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            2. Three warehouses, not two
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Every order asks for three separate warehouse roles, all
            required, regardless of method.
          </p>
          <Definitions
            items={[
              {
                term: "Raw Material Warehouse",
                desc: "Where the materials physically sit before production starts.",
              },
              {
                term: "Production Warehouse",
                desc: "Where materials land once they're actually issued, and where they get consumed from during the run.",
              },
              {
                term: "Finished Goods Warehouse",
                desc: "Where the output lands once submitted. This is fixed here at creation, there's no way to pick a different warehouse for it later. Settings → Organization defaults does not fill these three — you pick them on every order.",
              },
            ]}
          />
          <Screenshot caption="Screenshot: the three warehouse fields on the Add Production form." />

          {/* 3. What happens on save */}
          <h3
            id="section-0-sub-2"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            3. What actually happens when you save depends on the method
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Saving doesn&apos;t touch stock either way, but the two methods
            start their orders in genuinely different states.
          </p>
          <Definitions
            items={[
              {
                term: "BOM Based orders",
                desc: "Start as Waiting for Inventory Approval, and a real Inventory Transfer Request is created behind the scenes for the raw material list.",
              },
              {
                term: "Process Route orders",
                desc: "Start as Pending, with no approval step. There's nothing to wait on, you move straight into Submit Production.",
              },
            ]}
          />
          <Callout type="warning">
            Approving that inventory transfer request doesn&apos;t deduct raw
            material stock, it moves it from the Raw Material Warehouse into
            the Production Warehouse. The actual deduction only happens
            later, when the run is submitted. Rejecting the request instead
            sets the order to Inventory Rejected, and it can be re-raised for
            approval again from there.
          </Callout>
          <Screenshot caption="Screenshot: a BOM Based order sitting at Waiting for Inventory Approval, with its linked Inventory Transfer Request." />
        </motion.div>

        <hr className="my-8" />

        {/* Business rules */}
        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-1"
            className="text-[24px] sentient-medium text-[#111827] mb-4 scroll-mt-24"
          >
            Business rules
          </h2>
          <div className="border border-[#E5E5E5] rounded-[14px] overflow-hidden bg-white overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-[#F5F5F5] bg-[#F9F9F9]">
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Topic
                  </th>
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Rule
                  </th>
                </tr>
              </thead>
              <tbody>
                {RULES.map((r, i) => (
                  <tr key={r.topic} className={i < RULES.length - 1 ? "border-b border-[#F5F5F5]" : ""}>
                    <td className="px-5 py-3 text-[14px] geist-medium text-[#111827] align-top w-[220px]">
                      {r.topic}
                    </td>
                    <td className="px-5 py-3 text-[14px] geist-regular text-[#374151]">{r.rule}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>

        {/* Related */}
        <motion.div variants={fadeUp} className="flex flex-col sm:flex-row gap-4 flex-wrap">
          <Link
            href="/documentation/books/production/submit-production"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] transition-colors mb-1 flex items-center gap-1.5">
              Submit Production <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#6B7280]">
              Where a saved order actually gets completed and costed
            </p>
          </Link>
          <Link
            href="/documentation/books/all-master/item-master"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] transition-colors mb-1 flex items-center gap-1.5">
              Item Master <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#6B7280]">
              Where an item's BOM or Recipe is actually configured
            </p>
          </Link>
          <Link
            href="/documentation/books/all-master/recipe"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] transition-colors mb-1 flex items-center gap-1.5">
              Recipe / BoM <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#6B7280]">
              The two different concepts a Process Route order draws from
            </p>
          </Link>
        </motion.div>
      </motion.div>
    </DocsPageFrame>
  );
}
